import { Injectable } from '@angular/core';
import { NavigationService } from '@services/nav/nav.service';
import { LeapSdkService } from '../../../public/leap/leap-sdk.service';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { UrlResult, UrlService } from '@services/url/url.service';
import { Actions, ofActionSuccessful } from '@ngxs/store';
import { take } from 'rxjs/operators';
import LogRocket from 'logrocket';
import { BusinessState } from '@state/business';
import { MatterRepo } from '@repos/matter/matter.repo';
import { SnackbarService } from '@services/ui/snackbar.service';
import { ICON } from '@core/util/icon';
import { Params } from '@angular/router';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';
import { GlobalProfileComponent } from 'src/app/private/global-profile/global-profile.component';
import { GlobalProfileNavType } from 'src/app/private/global-profile/global-profile.types';
import { LeapDispatch } from 'src/app/public/leap/state/leap.dispatch';
import {
	LeapWindowAction,
	LeapWindowType,
} from 'src/app/public/leap/state/leap.model';
import { SendCrossWindowMessage } from 'src/app/public/leap/state/leap.actions';
import { MatterSource } from '@models/matter';

@Injectable({
	providedIn: 'root',
})
class CloserNavigationService {
	constructor(private navService: NavigationService) {}

	goToMatter(matterId: string, url: UrlResult, params?: Params): void {
		this.navService.goToUrl(url || UrlService.Matter(matterId), params);
	}
}

@Injectable({
	providedIn: 'root',
})
class LeapNavigationService {
	@SelectSnapshot(BusinessState.businessId) businessId: string;
	constructor(
		private leapSDKService: LeapSdkService,
		private actions$: Actions,
		private matterRepo: MatterRepo,
		private snackbarService: SnackbarService,
		private leapDispatch: LeapDispatch,
	) {}

	async goToMatter(
		matterId: string,
		url: UrlResult,
		params?: Params,
	): Promise<void> {
		this.actions$
			.pipe(ofActionSuccessful(SendCrossWindowMessage), take(1))
			.subscribe(async () => {
				try {
					const matter = await this.matterRepo
						.GetMatterById(matterId)
						.toPromise();

					const matterSource = matter.source;
					const leapMatter = matterSource === MatterSource.LEAP;

					if (leapMatter) {
						const leapMatterId = matter.leapMatterId;

						if (!leapMatterId) {
							throw new Error('Matter does not have a LEAP matter ID');
						}

						await this.openInLeap(leapMatterId);
					} else {
						this.alertUser();
					}
				} catch (error) {
					this.snackbarService.openSnackBar({
						text: 'An error occurred while trying to open the matter.',
						type: 'error',
						icon: ICON.CIRCLE_X,
					});
					LogRocket.error('Error navigating to matter:', matterId, error);
				}
			});

		LogRocket.log(
			`The application should navigate to ${url}. This url will be stored in local storage`,
		);

		this.leapDispatch.SendCrossWindowMessage(
			{
				action: LeapWindowAction.OPENING_MATTER_URL,
				to: LeapWindowType.MATTER_WINDOW,
				data: {
					url,
					params: params ?? undefined,
					relatedMatterId: matterId,
				},
			},
			undefined,
			false,
		);
	}

	private async openInLeap(leapMatterId: string) {
		const sdk = await this.leapSDKService.GetLeapSDK();
		const request = {
			matterId: leapMatterId,
		};

		sdk.matter.openMatter(request);
	}

	private alertUser() {
		this.snackbarService.openSnackBar({
			text: 'This matter was created in Closer and cannot be opened in LEAP.',
			type: 'error',
			icon: ICON.CIRCLE_X,
		});
	}
}

@Injectable({
	providedIn: 'root',
})
export class MultipurposeNavigationService {
	private loadedFromLeap = false; // FT: We don't need this so hardcode to false in case it is called. Cleanup later

	constructor(
		private closerNavigationService: CloserNavigationService,
		private leapNavigationService: LeapNavigationService,
		private drawerService: DrawerService,
	) {}

	async goToMatter(
		matterId: string,
		url?: string,
		params?: Params,
	): Promise<void> {
		url = url || UrlService.Matter(matterId);
		if (this.loadedFromLeap) {
			await this.leapNavigationService.goToMatter(matterId, url, params);
		} else {
			this.closerNavigationService.goToMatter(matterId, url, params);
		}
	}

	openClientDrawerWithOptionalTab(
		clientId?: string,
		tab?: GlobalProfileNavType,
	) {
		this.drawerService.openDrawer(GlobalProfileComponent, {
			data: { clientId, tab },
		});
	}
}
